import { Tooltip } from 'antd'
import axios from 'axios'
import React, { FunctionComponent, useCallback, useState } from 'react'

import { PROPERTY_TYPES } from '../../../common/drmTypes'
import { useAppSelector } from '../../../redux/hooks'
import {
  propertyDetailPath,
  requestGetParcelaIdPath,
  requestPropertyRuianPath,
} from '../../utils/paths'
import { PropertyListAreaDetailRow } from '../../utils/useFetchPropertyListDetailArea'
import { useSetPropertyDrawer } from '../../utils/useSetPropertyDrawer'

export interface PropertyListDetailPropertyDetailLinkProps {
  propertyCode: string
  propertyType: PROPERTY_TYPES
  buttonText?: string
  areaRecord?: PropertyListAreaDetailRow
}

export const PropertyListDetailPropertyDetailLink: FunctionComponent<PropertyListDetailPropertyDetailLinkProps> =
  ({
    propertyCode,
    areaRecord,
    propertyType,
    buttonText = 'Zobrazit detail',
  }) => {
    const setDrawer = useSetPropertyDrawer()
    const token = useAppSelector(store => store.myProfile.token)

    const [loading, setLoading] = useState(false)
    const [loadedId, setLoadedId] = useState<string | null>(null)
    const [error, setError] = useState<string | null>(null)

    const fetchBuildingId = useCallback(
      async (force?: boolean) => {
        if ((!force && loading) || loadedId) return loadedId
        setError(null)
        try {
          setLoading(true)
          const requestPath = requestPropertyRuianPath(
            propertyType,
            token,
            propertyCode
          )
          const response = await axios.get(requestPath)
          const id = response.data?.id || response.data?.[0]?.id
          if (!id) throw new Error()

          setLoadedId(id)
          return id as string
        } catch (e: unknown) {
          setError('Chyba při načítání detailu')
          return null
        } finally {
          setLoading(false)
        }
      },
      [loadedId, loading, propertyCode, propertyType, token]
    )

    const fetchAreaId = useCallback(
      async (force?: boolean) => {
        if ((!force && loading) || loadedId) return loadedId
        setError(null)
        try {
          setLoading(true)

          const response = await axios.post(requestGetParcelaIdPath(), {
            token,
            katastralniUzemiKod: areaRecord?.katastralniuzemi,
            kmenoveCislo: areaRecord?.kmenovecislo,
            pododdeleniCisla: areaRecord?.poddelenicisla,
            druhCislovaniKod: areaRecord?.druhcislovanikod,
          })

          const id =
            response.data?.id || response.data?.[0]?.id || response.data
          if (!id) throw new Error()

          setLoadedId(id)
          return id as string
        } catch (e: unknown) {
          setError('Chyba při načítání detailu')
          return null
        } finally {
          setLoading(false)
        }
      },
      [
        areaRecord?.druhcislovanikod,
        areaRecord?.katastralniuzemi,
        areaRecord?.kmenovecislo,
        areaRecord?.poddelenicisla,
        loadedId,
        loading,
        token,
      ]
    )

    const fetchId = useCallback(
      async (force?: boolean) =>
        propertyType === PROPERTY_TYPES.BUILDING
          ? await fetchBuildingId(force)
          : await fetchAreaId(force),
      [fetchAreaId, fetchBuildingId, propertyType]
    )

    const handleClick = useCallback(
      async (e: React.MouseEvent<HTMLAnchorElement>) => {
        let id = loadedId

        if (!id) {
          id = await fetchId(true)
          if (!id) return e.preventDefault()
        }

        if (e.button === 0 && !e.ctrlKey && !e.metaKey) {
          e.preventDefault()
          setDrawer(id, propertyType)
        }
      },
      [fetchId, loadedId, propertyType, setDrawer]
    )

    const buttonElement = (
      <a
        href={loadedId ? propertyDetailPath(propertyType, loadedId) : undefined}
        onClick={handleClick}
        onMouseEnter={() => !loadedId && !error && fetchId()}
        className="px-3 py-1.5"
      >
        {buttonText}
      </a>
    )

    if (error)
      return (
        <Tooltip title={error} open={!!error}>
          {buttonElement}
        </Tooltip>
      )

    return buttonElement
  }
